import { Box, Chip } from '@mui/material';
import type { ReactNode } from 'react';

/** The chip's geometry, shared by the count and the icon. `pointerEvents: none`: it is decoration inside a tab, so a click on it has to reach the control underneath. */
const CHIP_SX = {
  height: 20,
  fontSize: '0.6875rem',
  fontWeight: 600,
  pointerEvents: 'none',
  '& .MuiChip-label': { px: 0.75 },
} as const;

/**
 * A tab label with the number of rows behind it, as a chip. The page strip's are
 * filled and the subtabs' outlined, so the two levels stay told apart. A count still
 * loading renders as nothing; a zero is greyed rather than blue, which would claim
 * there is something behind the tab.
 */
export const tabLabelWithCount = (
  label: ReactNode,
  count: number | undefined,
  variant: 'filled' | 'outlined' = 'filled',
): ReactNode => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {label}
    {count === undefined ? null : (
      <Chip
        label={count}
        size="small"
        variant={variant}
        color={count === 0 ? 'default' : 'primary'}
        sx={{
          ...CHIP_SX,
          // `default` alone greys the fill and the border but leaves the number at
          // full-strength body ink, darker than the label it belongs to.
          ...(count === 0 && { color: 'text.secondary' }),
        }}
      />
    )}
  </Box>
);

/**
 * A tab label with an icon where its neighbours carry a count — for a tab that holds
 * figures rather than rows. No chip around it: a chip is the shape a count takes here,
 * and an empty one reads as a count still loading.
 */
export const tabLabelWithIcon = (label: ReactNode, icon: ReactNode): ReactNode => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {label}
    <Box
      component="span"
      sx={{ display: 'inline-flex', pointerEvents: 'none', color: 'primary.main' }}
    >
      {icon}
    </Box>
  </Box>
);

/** Undefined until every part is known, so the number never counts up in steps. */
export const sumCounts = (...counts: (number | undefined)[]): number | undefined =>
  counts.some((count) => count === undefined)
    ? undefined
    : counts.reduce<number>((total, count) => total + (count ?? 0), 0);
